import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Circle, Clock, ExternalLink, Maximize2, ShieldCheck, ListChecks, CalendarClock } from "lucide-react";
import { BlueVaseTracker } from "./BlueVaseTracker";
import PathToPrime from "@/pages/agent/PathToPrime";

const ALT = "Retail Traditional Agent Roadmap to Success, District 88 SF Bay: six phases — Foundation & Approval, Licensing & Training, Staffing & Office Setup, Production & Business Development, Grow & Expand, Advanced Business Building — with Blue Vase shown at Grow & Expand, after full-time Career appointment. Time ranges are approximate.";

function Illustration({ src }) {
  const [open, setOpen] = useState(false);
  return (
    <figure className="rounded-2xl overflow-hidden border border-slate-200 bg-white" data-testid="rt-illustration">
      <button type="button" onClick={() => setOpen(true)} className="relative block w-full group" data-testid="rt-illustration-enlarge" aria-label="Enlarge roadmap illustration">
        <img src={src} alt={ALT} className="w-full h-auto" loading="lazy" />
        <span className="absolute right-3 bottom-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-[#013A5A] shadow group-hover:bg-white"><Maximize2 className="w-3.5 h-3.5" />Enlarge</span>
      </button>
      <figcaption className="px-4 py-2 text-[11px] text-slate-500">Illustration for orientation. The My Checklist tab holds the official, current requirements. Time ranges are approximate planning guidance — not deadlines.</figcaption>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[96vw] w-[1400px] p-2" data-testid="rt-illustration-dialog">
          <DialogHeader className="sr-only"><DialogTitle>Retail Traditional roadmap</DialogTitle></DialogHeader>
          <div className="overflow-auto max-h-[85vh]"><img src={src} alt={ALT} className="w-full min-w-[900px] h-auto" /></div>
        </DialogContent>
      </Dialog>
    </figure>
  );
}

function ProgressCards({ d }) {
  const c = d.candidate_progress, o = d.official;
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div className="rounded-xl bg-[#013A5A] text-white p-4" data-testid="rt-official-progress">
        <p className="text-[11px] uppercase tracking-wider text-[#5EEAD4] flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />Official verified program progress</p>
        <p className="text-3xl font-bold mt-1">{o.progress}%</p>
        <p className="text-xs text-white/70">{o.required_complete}/{o.required_total} required milestones verified by your district team</p>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="rt-candidate-progress">
        <p className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" />Your checklist</p>
        <p className="text-3xl font-bold mt-1 text-slate-900">{c.done}<span className="text-base text-slate-400"> / {c.total}</span></p>
        <p className="text-xs text-slate-500">Required items you've completed. Completing items never grants appointment, licensing clearance or permission to sell.</p>
      </div>
    </div>
  );
}

const ICON = (t) => t.is_done ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : t.status === "submitted" ? <Clock className="w-4 h-4 text-[#006090]" /> : t.status === "in_progress" ? <Clock className="w-4 h-4 text-amber-600" /> : <Circle className="w-4 h-4 text-slate-300" />;
const STATUS = { completed: "Completed", submitted: "Submitted", in_progress: "In progress", needs_attention: "Needs attention", not_started: "Not started" };

function TaskRow({ t, readOnly, reload, goTo }) {
  const complete = async () => { try { await api.post(`/portal/tasks/${t.id}/complete`); toast.success("Marked complete"); reload(); } catch (e) { toast.error(apiError(e)); } };
  const toggle = async (c) => { try { await api.patch(`/portal/tasks/${t.id}/checklist/${c.id}`, { done: !c.done }); reload(); } catch (e) { toast.error(apiError(e)); } };
  const dest = t.portal_action === "blue_vase" ? null : t.portal_action;
  return (
    <li className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`rt-task-${t.id}`}>
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5">{ICON(t)}</span>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${t.is_done ? "text-slate-500" : "text-slate-900"}`}>{t.display_title}
            {!t.required && <span className="ml-2 text-[10px] font-bold uppercase text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">Optional</span>}</p>
          {t.completion_criteria && <p className="text-xs text-slate-500 mt-0.5"><span className="font-semibold">Done when:</span> {t.completion_criteria}</p>}
          {t.carried_forward && <p className="text-[11px] text-emerald-700 mt-0.5">{t.partial_carry ? "Earlier work is attached — finish the remaining items." : "Carried forward from your earlier completed work."}</p>}
          {t.checklist?.length > 0 && <ul className="mt-2 space-y-1">{t.checklist.map((c) => (
            <li key={c.id}><label className="flex items-start gap-2 text-xs text-slate-700"><input type="checkbox" checked={c.done} disabled={readOnly || t.is_done} onChange={() => toggle(c)} className="mt-0.5" data-testid={`rt-check-${c.id}`} />{c.text}</label></li>))}</ul>}
          <div className="mt-2 flex flex-wrap gap-2">
            {t.vendor_doc_url && <a href={t.vendor_doc_url} target="_blank" rel="noreferrer" className="text-xs text-[#006090] inline-flex items-center gap-1" data-testid={`rt-vendor-${t.id}`}>Official vendor help <ExternalLink className="w-3 h-3" /></a>}
            {dest && goTo && <button onClick={() => goTo(dest)} className="text-xs text-[#006090] underline" data-testid={`rt-go-${t.id}`}>Open {dest}</button>}
            {(t.needs_submission) && goTo && <button onClick={() => goTo("tasks")} className="text-xs text-[#006090] underline">Upload in My Tasks</button>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="text-[10px] font-semibold uppercase text-slate-500">{STATUS[t.status] || t.status}</span>
          {!readOnly && !t.is_done && !t.needs_submission && t.status !== "submitted" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={complete} data-testid={`rt-complete-${t.id}`}>Mark complete</Button>}
        </div>
      </div>
    </li>
  );
}

function PhasePanel({ p, d, readOnly, reload, goTo }) {
  const sys = p.key === "production";
  return (
    <div className="space-y-4" data-testid={`rt-phase-${p.key}`}>
      <div className="rounded-xl bg-slate-50 p-4">
        <p className="text-sm text-slate-700">{p.purpose}</p>
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" />{p.timing} · approximate planning guidance</span>
          <span data-testid={`rt-phase-progress-${p.key}`}>{p.required_done}/{p.required_total} required done{p.optional_total ? ` · ${p.optional_total} optional` : ""}</span>
          {p.resources.map(([l, k]) => goTo && <button key={k} onClick={() => goTo(k)} className="text-[#006090] underline">{l}</button>)}
        </div>
        {sys && <p className="mt-2 text-xs text-slate-600" data-testid="rt-systems-target">Systems setup: {d.systems.label} — operational within {d.systems.target_days} days of full-time Career appointment{d.systems.target_date ? ` (target ${d.systems.target_date})` : ""}. Start preparing now.</p>}
      </div>
      {p.key === "grow" && <BlueVaseTracker bv={d.blue_vase} readOnly={readOnly || !d.blue_vase.window} onChanged={reload} />}
      <ul className="space-y-2">{p.tasks.map((t) => <TaskRow key={t.id} t={t} readOnly={readOnly || (t.portal_action === "blue_vase" && !d.blue_vase.window)} reload={reload} goTo={goTo} />)}</ul>
      {!p.tasks.length && <p className="text-sm text-slate-400">No checklist items in this phase yet.</p>}
    </div>
  );
}

export function RetailRoadmap({ data, goTo }) {
  return (
    <div className="space-y-6" data-testid="rt-roadmap">
      <Illustration src={data.image_url} />
      <section data-testid="rt-overview">
        <h2 className="font-display text-xl font-semibold text-slate-900">Your Retail Traditional roadmap</h2>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">Six phases take you from approval and licensing through opening your office, producing as a full-time Career Agent, and growing your agency long term. Every phase is open to you now, so you can look ahead at any time. Viewing a phase never changes your official program status or authorizes selling. Blue Vase comes after basic Reserve training, once you're a full-time Career Agent (Grow & Expand).</p>
        <ol className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.phases.map((p, i) => (
            <li key={p.key} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`rt-overview-${p.key}`}>
              <p className="text-xs font-bold text-[#006090]">PHASE {i + 1}</p>
              <p className="font-semibold text-slate-900">{p.name}</p>
              <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1"><CalendarClock className="w-3 h-3" />{p.timing} · approximate</p>
              <p className="text-xs text-slate-600 mt-2">{p.purpose}</p>
            </li>))}
        </ol>
        {goTo && <Button className="mt-4 bg-[#006090] hover:bg-[#004E75]" onClick={() => goTo("journey", "checklist")} data-testid="rt-open-checklist">Open My Checklist</Button>}
      </section>
    </div>
  );
}

export function RetailChecklist({ data, reload, goTo }) {
  const { viewAs } = useAuth();
  const readOnly = (!!viewAs && !viewAs.login_as) || data.enrollment_status === "ended";
  return (
    <div className="space-y-5" data-testid="rt-checklist">
      <ProgressCards d={data} />
      <Tabs defaultValue="foundation">
        <TabsList className="h-auto grid grid-cols-2 sm:grid-cols-3 gap-1 bg-slate-100 p-1 w-full" data-testid="rt-tabs">
          {data.phases.map((p, i) => (
            <TabsTrigger key={p.key} value={p.key} className="text-xs sm:text-sm px-2 py-2 whitespace-normal text-left justify-start" data-testid={`rt-tab-${p.key}`}>
              <span className="font-bold mr-1.5 text-[#006090]">{i + 1}</span>{p.name}<span className="ml-1.5 text-[10px] text-slate-400">{p.required_done}/{p.required_total}</span>
            </TabsTrigger>))}
        </TabsList>
        {data.phases.map((p) => <TabsContent key={p.key} value={p.key} className="mt-4"><PhasePanel p={p} d={data} readOnly={readOnly} reload={reload} goTo={goTo} /></TabsContent>)}
      </Tabs>
      <div className="h-16 sm:h-0" />
    </div>
  );
}

export function useRetailRoadmap() {
  const [data, setData] = useState(undefined);
  const load = useCallback(() => { api.get("/portal/rt-roadmap").then((r) => setData(r.data)).catch(() => setData({ applicable: false })); }, []);
  useEffect(() => { load(); }, [load]);
  return [data, load];
}

export default function MyRoadmapPage() {
  const [data, load] = useRetailRoadmap();
  const { isOfficeUser } = useAuth();
  if (data === undefined) return <p className="text-sm text-slate-400 p-6">Loading your roadmap…</p>;
  if (!data.applicable && isOfficeUser) return <PathToPrime />;
  if (!data.applicable) return <p className="text-sm text-slate-500 p-6" data-testid="rt-roadmap-none">No program roadmap is linked to your account.</p>;
  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6"><h1 className="font-display text-3xl font-bold text-slate-900 mb-4">My Roadmap</h1>
      <Tabs defaultValue="roadmap"><TabsList data-testid="agent-roadmap-tabs"><TabsTrigger value="roadmap">My Roadmap</TabsTrigger><TabsTrigger value="checklist" data-testid="agent-tab-checklist">My Checklist</TabsTrigger></TabsList>
        <TabsContent value="roadmap" className="mt-4"><RetailRoadmap data={data} /></TabsContent>
        <TabsContent value="checklist" className="mt-4"><RetailChecklist data={data} reload={load} /></TabsContent></Tabs>
    </div>);
}
